import React, { useContext, useEffect } from "react";
import ComponentsService from "../../services/ComponentsService";
import {
  ComponentStatus,
  ComponentTemplateDto,
} from "@growth-machine-llc/stridist-api-client";
import { useComponentTemplateListFilterContext } from "../../contexts/ComponentTemplateLibraryFilterContext";
import {
  Box,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { EditorProgramContext } from "../new-editor/hooks";
import SidebarTemplate, {
  TEMPLATE_SIDEBAR_WIDTH,
} from "./sidebar/SidebarTemplate";
import { useTemplateSidebar } from "../../contexts/ComponentTemplateSidebarContext";
import { useComponentTemplateDialog } from "../../contexts/ComponentTemplateDialogContext";
import { JourneyAnchors, mapLibraryPrivacyOptions } from "../../constants";
import { JourneyRunContext } from "../../contexts/JourneyRunContext";
import {
  ComponentTemplateLibraryCardSkeleton,
  ComponentTemplateLibraryTableSkeleton,
} from "../loading/ComponentTemplateLibrarySkeleton";
import ComponentTemplateRow from "./ComponentTemplateRow";
import { useQuery } from "@tanstack/react-query";
import { CategoryFilter } from "./CategoryFilter";
import { ComponentTemplateCardView } from "./ComponentTemplateCardView";
import { getCommonTransition } from "../../utils/mui";
import { CoachLibraryTableScreen } from "../library/CoachLibraryTableScreen";
import { NameSortButton } from "../exercise-library/filters/NameSortButton";
import { useCurrentUser } from "../../hooks/useCurrentUser";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(1.5),
    padding: 1,
    boxShadow: theme.shadows[4],
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      boxShadow: "none",
    },
  },
}));

export interface ComponentTemplateLibraryProps {
  status: ComponentStatus;
}

export const getComponentTemplateLibraryWidths = () => {
  return {
    thumbnail: "16%",
    title: "54%",
    category: "20%",
    type: "5%",
    actions: "5%",
  };
};

export const COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY =
  "component-templates-library";

const ComponentTemplateLibrary = (props: ComponentTemplateLibraryProps) => {
  const { status } = props;
  const { isOpen: isSidebarOpen, openSidebar } = useTemplateSidebar();

  const { openDialog } = useComponentTemplateDialog();
  const { isRunning } = useContext(JourneyRunContext);

  const user = useCurrentUser();

  const columnWidths = getComponentTemplateLibraryWidths();

  const theme = useTheme();
  const downSlg = useMediaQuery(theme.breakpoints.down("slg"));
  const downMd = useMediaQuery(theme.breakpoints.down("md"));

  const s = useStyles();
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ComponentTemplateDto>(null);

  const {
    query,
    sortKey,
    privacy,
    page,
    rowsPerPage,
    templateType: templateTypeContext,
    handleComponentTemplateTypeChange,
    handleSortKeyChange,
    handlePageChange,
    handleRowsPerPageChange,
  } = useComponentTemplateListFilterContext();

  const componentType =
    templateTypeContext === "ALL" ? null : templateTypeContext;

  const { data: componentTemplatesData, isFetching } = useQuery({
    queryKey: [
      COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY,
      {
        componentType,
        query,
        privacy,
        page,
        rowsPerPage,
        sortKey,
        status,
      },
    ],
    queryFn: () =>
      ComponentsService.getLibraryComponentTemplates(
        status,
        componentType,
        query,
        mapLibraryPrivacyOptions(privacy),
        page,
        rowsPerPage,
        sortKey,
      ),
  });

  const templates = componentTemplatesData?.items || [];

  React.useEffect(() => {
    if (templates?.length > 0) {
      setSelectedTemplate(templates[0]);
      return;
    }
  }, [templates]);

  const handleTemplateClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const {
        dataset: { templateId },
      } = event.currentTarget;
      event.stopPropagation();
      event.preventDefault();

      const template = templates?.find((it) => it.id === templateId);
      setSelectedTemplate(template);
      if (!downSlg) {
        openSidebar();
      } else {
        openDialog(template);
      }
    },
    [templates, setSelectedTemplate, selectedTemplate, downSlg],
  );

  const handleOpenDialog = React.useCallback(
    (template: ComponentTemplateDto, editMode?: boolean) => {
      openDialog(template, editMode);
    },
    [],
  );

  useEffect(() => {
    if (isRunning && selectedTemplate && !downMd) {
      openSidebar();
    }
  }, [isRunning, selectedTemplate, openSidebar, downMd]);

  const renderTableHead = React.useCallback(
    () =>
      !downMd && (
        <TableHead>
          <TableRow>
            <TableCell width={columnWidths.thumbnail}>Thumbnail</TableCell>
            <TableCell width={columnWidths.title}>
              <NameSortButton
                sortKey={sortKey}
                onChangeSortKey={(nextSort) => {
                  handleSortKeyChange(nextSort);
                  handlePageChange(1);
                }}
              />
            </TableCell>
            <TableCell align="center" sx={{ width: columnWidths.category }}>
              <CategoryFilter
                onChange={handleComponentTemplateTypeChange}
                contextFilters={componentType}
              />
            </TableCell>
            <TableCell align="center" width={columnWidths.type}>
              Access
            </TableCell>
            <TableCell align="center" width={columnWidths.actions}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
      ),
    [
      downMd,
      columnWidths,
      sortKey,
      handleSortKeyChange,
      handlePageChange,
      handleComponentTemplateTypeChange,
      componentType,
    ],
  );

  const renderRow = React.useCallback(
    (item: ComponentTemplateDto, index: number) =>
      !downMd && (
        <ComponentTemplateRow
          type={item.type}
          className={index === 0 && JourneyAnchors.FIRST_COMPONENT_TEMPLATE}
          component={item}
          currentUser={{
            id: user.id,
            isTeamAccount: user.isTeamAccount,
            teamRole: user.teamRole,
          }}
          selected={selectedTemplate?.id === item.id && isSidebarOpen}
          onContentClick={handleTemplateClick}
          onContentDoubleClick={() => {
            setSelectedTemplate(item);
            handleOpenDialog(item);
          }}
          data-template-id={item.id}
          onEdit={() => handleOpenDialog(item, true)}
        />
      ),
    [
      downMd,
      selectedTemplate,
      isSidebarOpen,
      handleTemplateClick,
      handleOpenDialog,
      user,
    ],
  );

  const renderMobileFilters = React.useCallback(
    () =>
      downMd && (
        <CategoryFilter
          onChange={handleComponentTemplateTypeChange}
          contextFilters={componentType}
        />
      ),
    [downMd, handleComponentTemplateTypeChange, componentType],
  );

  const renderMobileCard = React.useCallback(
    (item: ComponentTemplateDto) =>
      downMd && (
        <ComponentTemplateCardView
          template={item}
          currentUser={{
            id: user.id,
            isTeamAccount: user.isTeamAccount,
            teamRole: user.teamRole,
          }}
          onContentClick={handleTemplateClick}
          onEdit={() => handleOpenDialog(item, true)}
        />
      ),
    [downMd, handleTemplateClick, handleOpenDialog, user],
  );

  const renderSkeletonTable = React.useCallback(
    (className?: string) =>
      !downMd &&
      Array.from({ length: rowsPerPage }).map((_, index) => (
        <React.Fragment key={index}>
          <ComponentTemplateLibraryTableSkeleton className={className} />
        </React.Fragment>
      )),
    [downMd, rowsPerPage],
  );

  const renderSkeletonCards = React.useCallback(
    () =>
      downMd &&
      Array.from({ length: rowsPerPage }).map((_, index) => (
        <React.Fragment key={index}>
          <ComponentTemplateLibraryCardSkeleton />
        </React.Fragment>
      )),
    [downMd, rowsPerPage],
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          position: "absolute",
          marginInline: "auto",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            position: "relative",
            gap: isSidebarOpen ? 4 : 0,
          }}
        >
          <Box
            sx={{
              marginBottom: 2,
              flexGrow: 1,

              zIndex: theme.zIndex.appBar,
              width: !isSidebarOpen
                ? "100%"
                : `calc(100% - ${TEMPLATE_SIDEBAR_WIDTH}px)`,
              mr: downSlg
                ? "auto"
                : !isSidebarOpen
                  ? `-${TEMPLATE_SIDEBAR_WIDTH}px`
                  : 0,
              transition: getCommonTransition(theme, "margin-right"),
            }}
          >
            <CoachLibraryTableScreen<ComponentTemplateDto>
              mode={downMd ? "mobile" : "desktop"}
              data={templates}
              isLoading={isFetching}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              page={page - 1}
              onPageChange={(newPage) => handlePageChange(newPage + 1)}
              totalCount={componentTemplatesData?.totalCount || 0}
              renderTableHead={renderTableHead}
              renderRow={renderRow}
              renderMobileCard={renderMobileCard}
              renderSkeletonTable={renderSkeletonTable}
              renderSkeletonCards={renderSkeletonCards}
              renderMobileFilters={renderMobileFilters}
              emptyMessage="No component templates found for current filters."
              colCount={5}
              className={s.root}
            />
          </Box>
          {!downSlg && (
            <EditorProgramContext.Provider value={{ programId: 0 }}>
              <SidebarTemplate
                content={selectedTemplate}
                type={selectedTemplate?.type}
              />
            </EditorProgramContext.Provider>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ComponentTemplateLibrary;
